import type React from "react";
import { createContext, useContext, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { RocketLaunch } from "@mui/icons-material";
import { Database } from "@phosphor-icons/react";
import { HTTPClient } from "../HTTPClient";
import { useSnackbar } from "./ToastProvider";

type LimitationPlanContextProps = {
  showLimitationPlanModal: () => void;
};

const LimitationPlanContext = createContext<LimitationPlanContextProps | null>(null);

const LimitationPlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation("", { keyPrefix: "views.limitation" });
  const [open, setOpen] = useState(false);
  const { defaultError } = useSnackbar();

  const showLimitationPlanModal = () => {
    setOpen(true);
  };

  const contextValue = {
    showLimitationPlanModal,
  };

  return (
    <LimitationPlanContext.Provider value={contextValue}>
      {children}
      <Dialog open={open}>
        <DialogContent dividers>
          <Box
            sx={{
              maxWidth: 600,
              margin: "auto",
              textAlign: "center",
              padding: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {t("premium_header")}{" "}
              <Typography
                component="span"
                variant="h6"
                color="secondary"
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                {t("premium_actions")}
                <RocketLaunch sx={{ marginLeft: 0.5, fontSize: 20 }} />
              </Typography>
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("current_plan_description")}
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <img
                    src="/assets/actions/google-review.svg"
                    width="18"
                    height="18"
                    alt="Google Review"
                  />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  primary={t("feature_google_reviews")}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <img src="/assets/actions/instagram.svg" width="18" height="18" alt="Instagram" />
                  &nbsp;
                  <img src="/assets/actions/tiktok.svg" width="18" height="18" alt="TikTok" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  primary={t("feature_social_media")}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <Database color="action" weight="bold" size="18px" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                  primary={t("feature_data_collection")}
                />
              </ListItem>
            </List>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("premium_advantage")}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => {
                HTTPClient.post("/premiumUpgrade")
                  .then(() => {
                    setOpen(false);
                    window.premium = true;
                  })
                  .catch(defaultError);
              }}
            >
              {t("button_upgrade")}
            </Button>

            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom
              sx={{ mt: 3, cursor: "pointer" }}
              onClick={() => setOpen(false)}
            >
              {t("button_stay")}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </LimitationPlanContext.Provider>
  );
};

const useLimitationPlan = () => {
  const context = useContext(LimitationPlanContext);
  if (!context) {
    throw new Error("useLimitationPlan must be used within a LimitationPlanProvider");
  }
  return context;
};

export { LimitationPlanProvider, useLimitationPlan };
